// src/components/quizzes/MultipleChoice.js
//
// MULTIPLE CHOICE — new look (Nov 2026), same data and the same result:
//   questions[] = { question, questionImage?, answers: [{ text, image?, correct }] }
//   onComplete({ score, answers: details, details })   — details per question:
//     { qIndex, question, selectedText, correctText, correct }
//
// What it does
//   * one big, high-contrast question card and 2x2 chunky answer buttons
//     (A B C D, each its own colour) — readable from the back of a classroom
//   * 🔊 reads the question and every answer aloud (realistic cloud voice),
//     lighting each one up as it's read
//   * right: green + "You got it!"; wrong: shake, and the right answer lights
//     up so the question still teaches something
//   * progress bar, score and a 🔥 streak; music with a mute button
//
// All styles are "qk-" classes (kit/quizkit.css), so other games' CSS can't
// change how it looks — the cause of the old "weird, hard to read" text.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { playGameSound } from "../../services/gameSounds";
import { resolveImageSrc } from "../../utils/resolveImageSrc";
import { launchConfetti } from "../../utils/confetti";
import { sfx } from "./sfx";
import { LETTERS, QuizHud, useGameMusic, useReadAloud } from "./kit/QuizKit";

const MUSIC =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/gamemusic%2Fmultiple-choice.mp3?alt=media&token=0fb4b3ca-5778-4d14-b866-95cf37855d2d";
const RIGHT_SFX =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/sounds%2Fbongsound1.mp3?alt=media&token=f47df164-06b3-494e-ba16-5ab54f707306";
const WRONG_SFX =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/sounds%2Ftapsound1.mp3?alt=media&token=c60530e0-337c-4481-ae9c-87a64c076686";

const PRAISE = ["You got it! 🎉", "Great job! ⭐", "Yes! Correct! 🙌", "Awesome! 🚀", "Well done! 🌟"];
const KIND = ["Good try! 💪", "Almost! Here it is 👀", "Nice try — look! 👇", "Keep going! 💡"];

const play = (url) => {
  try {
    const a = sfx(url);
    a.currentTime = 0;
    a.play().catch(() => {});
  } catch {
    /* sound isn't worth breaking the game over */
  }
};

// The letter and the answer are read as two pieces ("Ay." then "Earth"):
// answers are shuffled, so each answer's voice is made once and reused under
// any letter, and the six letters are made once for the whole app.
const answersOf = (q) => (q?.answers || []).filter((a) => a && (a.text || a.image));
const partsFor = (q) =>
  q
    ? [
        { key: "q", kind: "question", text: q.question },
        ...answersOf(q).map((a, n) => ({
          key: n,
          kind: "answer",
          text: `${LETTERS[n]}. ${a.text || ""}`,
          say: a.text ? [`${LETTERS[n]}.`, a.text] : [`${LETTERS[n]}.`],
        })),
      ]
    : [];

const MultipleChoice = ({ questions = [], onComplete, topic }) => {
  const [i, setI] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [picked, setPicked] = useState(null); // index of the chosen answer
  const [feedback, setFeedback] = useState(null); // { good, text, key }
  const details = useRef([]);
  const usedVoice = useRef(false);
  const [muted, toggleMute, duck] = useGameMusic(MUSIC);
  const voice = useReadAloud(duck);

  const q = questions[i] || null;
  const answers = useMemo(() => answersOf(q), [q]);
  const parts = useMemo(() => partsFor(q), [q]);

  // Get this question's voice (and the next one's) ready the moment it
  // appears, so 🔊 starts at once in the natural voice. Each sentence is made
  // once for everyone and saved, so this is nearly always a quick lookup.
  useEffect(() => {
    voice.stop();
    voice.preload([...partsFor(q), ...partsFor(questions[i + 1])]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);

  if (!q) return null;
  const reading = voice.partIndex === null ? null : parts[voice.partIndex]?.key;

  const choose = (n) => {
    if (picked !== null) return;
    voice.stop();
    const a = answers[n];
    const right = Boolean(a?.correct);
    setPicked(n);
    // uploaded sound (Layout Builder → 🎵 Game sounds), else the built-in one
    playGameSound(right ? "correct" : "wrong", "multipleChoice", () => play(right ? RIGHT_SFX : WRONG_SFX));
    if (right) {
      setScore((s) => s + 1);
      setStreak((s) => s + 1);
    } else setStreak(0);
    setFeedback({ good: right, text: right ? PRAISE[i % PRAISE.length] : KIND[i % KIND.length], key: Date.now() });

    details.current[i] = {
      qIndex: i,
      question: q.question,
      selectedText: a?.text ?? "",
      correctText: answers.find((x) => x.correct)?.text ?? "",
      correct: right,
    };

    setTimeout(
      () => {
        setFeedback(null);
        if (i + 1 < questions.length) {
          setPicked(null);
          setI(i + 1);
          return;
        }
        const final = details.current.filter((d) => d?.correct).length;
        if (final >= Math.ceil(questions.length * 0.8)) launchConfetti();
        const list = details.current.filter(Boolean);
        onComplete?.({ score: final, answers: list, details: list });
      },
      right ? 1100 : 2000 // a wrong answer stays longer so the right one can be seen
    );
  };

  const stateOf = (n) => {
    if (picked === null) return reading === n ? "is-reading" : "";
    if (answers[n]?.correct) return "is-right";
    if (n === picked) return "is-wrong";
    return "is-dim";
  };

  return (
    <div className="qk-scene">
      <div className="qk-wrap">
        <QuizHud title={topic?.title || ""} index={i} total={questions.length} score={score} streak={streak} muted={muted} onMute={toggleMute} />

        <section className={`qk-question ${reading === "q" ? "is-reading" : ""}`} key={`q${i}`}>
          <h2 className="qk-question-text">{q.question}</h2>
          {q.questionImage && <img className="qk-question-img" src={resolveImageSrc(q.questionImage)} alt="" draggable="false" />}
          {voice.can && (
            <button
              type="button"
              className={`qk-read ${voice.reading ? "is-on" : ""}`}
              onClick={() => {
                if (voice.reading) return voice.stop();
                usedVoice.current = true;
                return voice.read(parts);
              }}
              aria-label={voice.reading ? "Stop reading" : "Read the question to me"}
              title={voice.reading ? "Stop" : "Read it to me"}
            >
              {voice.reading ? "■" : "🔊"}
            </button>
          )}
        </section>

        <div className={`qk-answers ${answers.length === 3 ? "is-three" : ""}`} key={`a${i}`}>
          {answers.map((a, n) => {
            const img = a.image ? resolveImageSrc(a.image) : "";
            return (
              <button
                key={n}
                type="button"
                className={`qk-answer ${stateOf(n)}`}
                onClick={() => choose(n)}
                disabled={picked !== null}
                aria-label={`Answer ${LETTERS[n]}: ${a.text || "picture"}`}
              >
                <span className="qk-letter" aria-hidden="true">
                  {LETTERS[n]}
                </span>
                {img && <img className="qk-answer-img" src={img} alt="" draggable="false" />}
                {a.text && <span className="qk-answer-text">{a.text}</span>}
                {picked !== null && (a.correct || n === picked) && (
                  <span className="qk-mark" aria-hidden="true">
                    {a.correct ? "✅" : "❌"}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Right / wrong popup. Larger screens: it appears just below the
            answers (this slot keeps its space, so nothing jumps). Phones: it
            pops up over the answer buttons (quizkit.css). */}
        <div className="qk-feedback-slot">
          {feedback && (
            <div key={feedback.key} className={`qk-feedback ${feedback.good ? "is-good" : "is-bad"}`} role="status" aria-live="polite">
              {feedback.text}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MultipleChoice;
